'use client';

import { useState, useCallback, type PropsWithChildren } from 'react';

import type { PanelTypes } from '@/components/panels/panelRegistry';
import { WorkspaceContext } from '@/contexts/workspaceContext';

import {
  PanelSize,
  type SidePanelSize,
  type DashboardAiEntryStore,
  type DialogEntry,
  type DialogOptions,
  type DialogStore,
  type SnackbarOptions,
  type SnackbarStore,
  type SidePanelStore,
} from './workspaceContextTypes';

const DEFAULT_SNACKBAR_AUTO_HIDE_DURATION = 3000;

const getSnackbarAutoHideDuration = (options?: SnackbarOptions) =>
  options?.autoHideDuration === undefined
    ? DEFAULT_SNACKBAR_AUTO_HIDE_DURATION
    : options.autoHideDuration;

export const WorkspaceProvider = ({ children }: PropsWithChildren) => {
  const [dialogStack, setDialogStack] = useState<DialogEntry[]>([]);

  const openDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack,
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const backDialog = useCallback(() => {
    setDialogStack((stack) => stack.slice(0, -1));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogStack([]);
  }, []);

  const replaceDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack.slice(0, -1),
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const isDialogOpen = useCallback(
    (id?: string) => {
      if (id) {
        return dialogStack.some((d) => d.id === id);
      }
      return dialogStack.length > 0;
    },
    [dialogStack]
  );

  const sharedDialog: DialogStore = {
    stack: dialogStack,
    open: openDialog,
    back: backDialog,
    closeAll: closeAllDialogs,
    replace: replaceDialog,
    isOpen: isDialogOpen,
  };

  const [rightPanel, setRightPanel] = useState<SidePanelStore>({
    isOpen: false,
    isAbsolute: false,
    isResizable: false,
    isPersistent: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: (panelSize: SidePanelSize) =>
      setRightPanel((state) => ({ ...state, panelSize })),
    setResizedWidth: (resizedWidth: number) =>
      setRightPanel((state) => ({ ...state, resizedWidth })),
    setIsOpen: (
      isOpen: boolean,
      panelSize?: SidePanelSize,
      isAbsolute?: boolean,
      isResizable?: boolean,
      isPersistent?: boolean
    ) =>
      setRightPanel((state) => ({
        ...state,
        isOpen,
        isAbsolute: isAbsolute ?? false,
        isResizable: isOpen ? (isResizable ?? false) : state.isResizable,
        isPersistent: isPersistent ?? false,
        panelSize: isOpen ? (panelSize ?? PanelSize.Large) : state.panelSize,
      })),
    setComponent: (Component: PanelTypes) =>
      setRightPanel((state) => ({ ...state, Component })),
  });

  const [snackbar, setSnackbar] = useState<SnackbarStore>({
    isOpen: false,
    message: '',
    notificationId: 0,
    variant: 'default',
    autoHideDuration: DEFAULT_SNACKBAR_AUTO_HIDE_DURATION,
    showMessage: (message: React.ReactNode, options?: SnackbarOptions) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        notificationId: state.notificationId + 1,
        variant: 'default',
        action: options?.action,
        autoHideDuration: getSnackbarAutoHideDuration(options),
      })),
    showErrorMessage: (message: React.ReactNode, options?: SnackbarOptions) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        notificationId: state.notificationId + 1,
        variant: 'error',
        action: options?.action,
        autoHideDuration: getSnackbarAutoHideDuration(options),
      })),
    showWarningMessage: (message: React.ReactNode, options?: SnackbarOptions) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        notificationId: state.notificationId + 1,
        variant: 'default',
        action: options?.action,
        autoHideDuration: getSnackbarAutoHideDuration(options),
      })),
    hide: () =>
      setSnackbar((state) => ({
        ...state,
        isOpen: false,
        message: '',
        variant: 'default',
        action: undefined,
        autoHideDuration: DEFAULT_SNACKBAR_AUTO_HIDE_DURATION,
      })),
  });

  const [dashboardAiEntryVisible, setDashboardAiEntryVisible] = useState(false);
  const dashboardAiEntry: DashboardAiEntryStore = {
    isVisible: dashboardAiEntryVisible,
    setIsVisible: setDashboardAiEntryVisible,
  };

  return (
    <WorkspaceContext.Provider
      value={{ rightPanel, snackbar, sharedDialog, dashboardAiEntry }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
